import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';

import ChartHeader from './chart-header/ChartHeader';
import { LoadingSpinner } from '@/views/components/common';
import { DEFAULT_OPTIONS } from '@/views/components/charts/deprecated/charts/GrowthChart';
import ChartToolbar from '@/views/components/charts/ChartToolbar';
import { renderDrawdownChart, renderGrowthChart, renderRiskRewardChart } from './PerformanceCharts';

import { CHART_STARTING_CAPITAL, PERFORMANCE_CHART, TIME_PERIODS, TIME_RANGE_PRESET } from '@/utils/constants';
import { formatNumber } from '@/utils/i18n';
import { getExpandedPortfolioItems, getGraphStartDate, getRollingIntervalFromDates, mergeChartItems } from '@/utils/charts';
import getSeriesDataImpl from '@/utils/comparison/get-series-data';
import {
  getAvailableCharts,
  getChartWarning,
  getDrawdownChartState,
  getGrowthChartState,
  getRiskRewardChartState,
  handleQuickDateChange,
} from '../utils/chart-container-helpers';

import ComparisonItem from '@/types/comparison-item';
import User from '@/types/user';
import Comparison from '@/types/comparison';
import Locale from '@/types/locale';
import { DrawdownChartState, ExpandedPortfoliosData, GrowthChartState, MAX_CAPITAL, RiskRewardChartState } from '../types';
import { getGrowthChartOptions } from '@/views/components/charts/growth-chart/utils/chart-options';
import { getDateQuickRangeFromPreset } from '@/utils/time-range-presets';
import RebalanceFrequency from '@/types/rebalance-frequency';

type Props = {
  items: ComparisonItem[];
  onChartOptionsChange: (options: any, chart: PERFORMANCE_CHART) => void;
  comparison: Comparison;
  user: User;
  locale: Locale;
  dark?: boolean;
  readOnly?: boolean;
  expandedPortfoliosData: ExpandedPortfoliosData;
  isAddingToChart?: boolean;
  onToggle?: () => void;
};

const ChartContainer: React.FC<Props> = ({
  items,
  onChartOptionsChange,
  comparison,
  user,
  locale,
  dark,
  readOnly,
  expandedPortfoliosData,
  isAddingToChart,
}) => {
  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});
  const [selectedChart, setSelectedChart] = useState<PERFORMANCE_CHART>(PERFORMANCE_CHART.GROWTH);

  const updateChartOptions = useCallback(() => {
    const growthChart = getGrowthChartState(user, comparison);
    const drawdownChart = getDrawdownChartState(user, comparison);
    const riskRewardChart = getRiskRewardChartState(user, comparison);

    let startDate: Date | null = null;
    let { endDate } = DEFAULT_OPTIONS;
    let useMonthly = false;
    let rebalancePeriod: RebalanceFrequency | undefined;
    let monthsRange;

    switch (selectedChart) {
      case PERFORMANCE_CHART.GROWTH:
        startDate = growthChart.startDate;
        endDate = growthChart.endDate;
        useMonthly = growthChart.useMonthly ?? false;
        rebalancePeriod = growthChart.rebalancePeriod;
        break;
      case PERFORMANCE_CHART.DRAWDOWN:
        startDate = drawdownChart.startDate;
        endDate = drawdownChart.endDate;
        useMonthly = drawdownChart.useMonthly ?? false;
        break;
      case PERFORMANCE_CHART.RISK_REWARD:
        monthsRange = riskRewardChart.monthsRange;
        break;
    }

    const portfolioHoldingItems = getExpandedPortfolioItems(items, user.appConfig.appRegion, expandedPortfoliosData);
    const chartItems = mergeChartItems(items, portfolioHoldingItems);
    const interval = getRollingIntervalFromDates(startDate, endDate, items, useMonthly, comparison.shortHistory);
    const earliestStartDate = startDate !== null ? getGraphStartDate(chartItems, startDate) : null;

    const series = getSeriesDataImpl(
      chartItems,
      growthChart.capital,
      comparison.showBaseCurrencies,
      comparison.shortHistory,
      user.appConfig.localCurrency,
      startDate,
      earliestStartDate,
      endDate,
      interval,
      growthChart.targetROI,
      undefined,
      rebalancePeriod,
      false,
      useMonthly,
    );
    const growthChartOptions = getGrowthChartOptions({
      height: undefined,
      locale,
      animate: true,
    });

    setChartOptions({
      ...growthChartOptions,
      series,
    });
  }, [comparison, expandedPortfoliosData, items, locale, selectedChart, user]);

  useEffect(() => {
    updateChartOptions();
  }, [updateChartOptions]);

  // State update helpers
  const updateGrowthChartState = (newState: Partial<GrowthChartState>) => {
    const updated = { ...getGrowthChartState(user, comparison), ...newState };
    onChartOptionsChange(updated, PERFORMANCE_CHART.GROWTH);
  };

  const updateDrawdownChartState = (newState: Partial<DrawdownChartState>) => {
    const updated = { ...getDrawdownChartState(user, comparison), ...newState };
    onChartOptionsChange(updated, PERFORMANCE_CHART.DRAWDOWN);
  };

  const updateRiskRewardChartState = (newState: Partial<RiskRewardChartState>) => {
    const updated = { ...getRiskRewardChartState(user, comparison), ...newState };
    onChartOptionsChange(updated, PERFORMANCE_CHART.RISK_REWARD);
  };

  // Event handlers
  const handleCapitalChange = (value: string) => {
    const capital = value ? Math.min(Number(value), MAX_CAPITAL) : 0;
    updateGrowthChartState({ capital });
  };

  const handleBlurCapitalChange = () => {
    const growthChart = getGrowthChartState(user, comparison);
    if (growthChart.capital === undefined) {
      updateGrowthChartState({ capital: CHART_STARTING_CAPITAL });
    }
  };

  const handleChangeTargetROI = (value: string) => {
    updateGrowthChartState({ targetROI: value ? Number(value) : undefined });
  };

  const onChangeChart = (chart: PERFORMANCE_CHART) => {
    setSelectedChart(chart);
  };

  const handleQuickDateChangeWrapper = (dateQuickRange: TIME_PERIODS) => {
    const riskRewardChart = getRiskRewardChartState(user, comparison);
    const useMonthly = comparison?.options?.growth?.useMonthly ?? false; // Although we check for growth options, it actually applies to all charts
    const newState = handleQuickDateChange(dateQuickRange, comparison, riskRewardChart.monthsRange, items, useMonthly);

    switch (selectedChart) {
      case PERFORMANCE_CHART.GROWTH:
        updateGrowthChartState(newState as Partial<GrowthChartState>);
        break;
      case PERFORMANCE_CHART.DRAWDOWN:
        updateDrawdownChartState(newState as Partial<DrawdownChartState>);
        break;
      case PERFORMANCE_CHART.RISK_REWARD:
        updateRiskRewardChartState(newState as Partial<RiskRewardChartState>);
        break;
    }
  };

  const handleDateRangeChange = (startDate: Date | null, endDate: Date) => {
    switch (selectedChart) {
      case PERFORMANCE_CHART.GROWTH:
        updateGrowthChartState({ startDate, endDate });
        break;
      case PERFORMANCE_CHART.DRAWDOWN:
        updateDrawdownChartState({ startDate, endDate });
        break;
      case PERFORMANCE_CHART.RISK_REWARD:
        updateGrowthChartState({ startDate, endDate });
        break;
    }
  };

  const renderPerformanceChart = () => {
    switch (selectedChart) {
      case PERFORMANCE_CHART.GROWTH:
        return renderGrowthChart(user, comparison, items, !!dark, chartOptions, locale);
      case PERFORMANCE_CHART.RISK_REWARD:
        return renderRiskRewardChart(user, comparison, items, !!dark, expandedPortfoliosData);
      case PERFORMANCE_CHART.DRAWDOWN:
        return renderDrawdownChart(user, comparison, items, !!dark, expandedPortfoliosData);
      default:
        return null;
    }
  };

  // Main render
  const growthChart = getGrowthChartState(user, comparison);
  const drawdownChart = getDrawdownChartState(user, comparison);
  const riskRewardChart = getRiskRewardChartState(user, comparison);
  const dateQuickRangeValues = getDateQuickRangeFromPreset(TIME_RANGE_PRESET.MULTI_ITEMS_DEFAULT, comparison.shortHistory);
  const showPerformancePicker = getAvailableCharts(user, comparison).length > 1 && comparison.items.length > 0;
  const formattedCapital = formatNumber(growthChart.capital ?? DEFAULT_OPTIONS.capital);
  const chartWarning = getChartWarning(selectedChart, comparison, items, user);

  let currentStartDate: Date | null = null;
  let currentEndDate: Date = DEFAULT_OPTIONS.endDate;
  let currentDateQuickRange: TIME_PERIODS = TIME_PERIODS['3Y'];

  switch (selectedChart) {
    case PERFORMANCE_CHART.GROWTH:
      currentStartDate = growthChart.startDate;
      currentEndDate = growthChart.endDate;
      currentDateQuickRange = growthChart.dateQuickRange;
      break;
    case PERFORMANCE_CHART.DRAWDOWN:
      currentStartDate = drawdownChart.startDate;
      currentEndDate = drawdownChart.endDate;
      currentDateQuickRange = drawdownChart.dateQuickRange;
      break;
    case PERFORMANCE_CHART.RISK_REWARD:
      currentDateQuickRange = riskRewardChart.dateQuickRange;
      currentStartDate = growthChart.startDate;
      currentEndDate = growthChart.endDate;
      break;
  }

  const chartTitles: Partial<Record<PERFORMANCE_CHART, string>> = {
    [PERFORMANCE_CHART.GROWTH]: 'Charts.Growth.Title',
    [PERFORMANCE_CHART.RISK_REWARD]: 'RiskRewardPlot.Title',
    [PERFORMANCE_CHART.DRAWDOWN]: 'Charts.Drawdown.Title',
  };

  return (
    <div className={classNames('chart-container', 'no-sidebar', 'chart', { dark })}>
      {isAddingToChart && <LoadingSpinner className={classNames({ dark })} />}
      <ChartHeader
        selectedChart={selectedChart}
        chartTitles={chartTitles}
        showPerformancePicker={showPerformancePicker}
        chartPickerProps={{
          dark,
          value: selectedChart,
          onChange: onChangeChart,
          charts: getAvailableCharts(user, comparison),
        }}
        capital={growthChart.capital}
        formattedCapital={formattedCapital}
        readOnly={readOnly}
        dark={dark}
        onCapitalChange={handleCapitalChange}
        onBlurCapitalChange={handleBlurCapitalChange}
        toolbar={
          <ChartToolbar
            dark={dark}
            readOnly={readOnly}
            showInlineTitle={false}
            targetROI={growthChart.targetROI}
            onChangeTargetROI={handleChangeTargetROI}
            dateQuickRange={currentDateQuickRange}
            onQuickDateChange={handleQuickDateChangeWrapper}
            dateQuickRangeValues={dateQuickRangeValues}
            startDate={currentStartDate}
            endDate={currentEndDate}
            onDateRangeChange={handleDateRangeChange}
            locale={locale}
            chartWarning={chartWarning}
            headerOptions={{
              hideTargetRate: selectedChart === PERFORMANCE_CHART.RISK_REWARD || selectedChart === PERFORMANCE_CHART.DRAWDOWN,
              hideDateRange: selectedChart === PERFORMANCE_CHART.RISK_REWARD,
            }}
          />
        }
      />
      {renderPerformanceChart()}
    </div>
  );
};

export default ChartContainer;
